import { memo, useCallback, useMemo } from 'react';
import { Obstacle } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { useTranslatedOptions } from '@/modules/common/hooks';

const WALL_APPEARANCE_OPTIONS: { labelKey: string; value: Obstacle['appearance'] }[] = [
  { labelKey: 'interface:properties.obstacle.appearance_options.pillar', value: 'PILLAR' },
  { labelKey: 'interface:properties.obstacle.appearance_options.box', value: 'BOX' },
  {
    labelKey: 'interface:properties.obstacle.appearance_options.warning_zone',
    value: 'WARNING_ZONE',
  },
];

interface Props {
  selectedObstacles: Obstacle[];
}

function AppearanceComponent({ selectedObstacles }: Props) {
  const fddmSvc: IFddmService = useMemo(
    () => FddmServiceFactory.getInstance().createFddmService(),
    [],
  );

  const options = useTranslatedOptions(WALL_APPEARANCE_OPTIONS);

  // Shown when any selected obstacle has an appearance, only editable when all of them do
  const appearances = useMemo(
    () => selectedObstacles.flatMap(({ appearance }) => (appearance ? [appearance] : [])),
    [selectedObstacles],
  );
  const isPartialSelection = appearances.length !== selectedObstacles.length;

  const appearance = useMemo(() => getCommonOption(appearances), [appearances]);

  const onChange = useCallback(
    (newValue: Obstacle['appearance']) => {
      if (isPartialSelection) return;

      fddmSvc.updateObstacles(
        selectedObstacles.map((obstacle) => ({
          ...obstacle,
          appearance: newValue,
          wallMaterial: newValue === 'WALL' ? obstacle.wallMaterial ?? 'CONCRETE' : undefined,
        })),
      );
      fddmSvc.setPreference('obstacle.appearance', newValue);
    },
    [isPartialSelection, selectedObstacles, fddmSvc],
  );

  if (appearances.length === 0) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.obstacle.type'>
      <DisabledReasonTooltip
        reasonKey={
          isPartialSelection
            ? 'interface:properties.obstacle.partial_selection.only_with_appearance'
            : undefined
        }
      >
        <SelectInput
          disabled={isPartialSelection}
          options={options}
          onChange={onChange}
          value={appearance}
          showMixed
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

const Appearance = memo(AppearanceComponent);

export default Appearance;
